import React, { useEffect, useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { isEmpty } from "lodash";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import useLobby from "@/hooks/use-lobby";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Chip,
  useMediaQuery,
  Theme,
} from "@mui/material";
import { TrendingUp } from "@mui/icons-material";
import { LANG } from "@/types";

// Interface for props
interface WelcomeBoxProps {
  backgroundColor?: string;
  textColor?: string;
}

// Define modern chart colors
const COLORS = [
  "#644980", // Blue
  "#10b981", // Green
  "#f59e0b", // Amber
  "#ef4444", // Red
  "#8b5cf6", // Purple
  "#06b6d4", // Cyan
];

const BarChartComponent: React.FC<WelcomeBoxProps> = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const { gettenderStatus } = useLobby();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );
  const isTablet = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  const [tenderStatus, setTenderStatus] = useState<
    { status: string; total: number }[]
  >([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getTotaltenderQ = async () => {
      try {
        const Total = await gettenderStatus();

        if (!isEmpty(Total.totals)) {
          const formattedData = Object.entries(Total.totals).map(
            ([status, total]) => ({
              status,
              total: total as number,
            })
          );
          setTenderStatus(formattedData);
        } else {
          setTenderStatus([
            { status: t("statistics.finished"), total: 100 },
            { status: t("statistics.unfinished"), total: 0 },
          ]);
        }
      } catch (error) {
        console.error("Error fetching tender status:", error);
        setTenderStatus([
          { status: t("statistics.finished"), total: 100 },
          { status: t("statistics.unfinished"), total: 0 },
        ]);
      } finally {
        setLoading(false);
      }
    };

    getTotaltenderQ();
  }, []);

  const totalTenders = tenderStatus.reduce((sum, item) => sum + item.total, 0);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const percentage =
        totalTenders > 0 ? ((data.value / totalTenders) * 100).toFixed(1) : 0;

      return (
        <Box
          sx={{
            backgroundColor: "#ffffff",
            border: "1px solid #e5e7eb",
            borderRadius: 2,
            padding: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "#1f2937", mb: 0.5 }}
          >
            {data.name}
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b7280" }}>
            {data.value} ({percentage}%)
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const CustomLegend = ({ payload }: any) => {
    return (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: 2,
          mt: 2,
        }}
      >
        {payload?.map((entry: any, index: number) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              padding: "4px 8px",
              borderRadius: 1,
              backgroundColor: "#f9fafb",
              border: "1px solid #e5e7eb",
            }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: entry.color,
              }}
            />
            <Typography
              variant="body2"
              sx={{ fontSize: "0.75rem", color: "#374151" }}
            >
              {entry.value}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Card
      dir={locale === LANG.EN ? "ltr" : "rtl"}
      sx={{
        height: "100%",
        boxShadow: "none",
        borderRadius: 3,
        background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",

        overflow: "hidden",
        transition: "all 0.3s ease",
      }}
    >
      <CardContent
        sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Box
            sx={{
              width: 3,
              height: 24,
              background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
              borderRadius: 2,
              mr: locale === LANG.EN ? 2 : 0,
              ml: locale === LANG.EN ? 0 : 2,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              background: "linear-gradient(135deg, #1f2937 0%, #374151 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
            }}
          >
            {t("statistics.chartTitle")}
          </Typography>
        </Box>

        {/* Summary Stats */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 3,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TrendingUp sx={{ fontSize: 20, color: "#10b981" }} />
            <Typography variant="body2" sx={{ color: "#6b7280" }}>
              {t("statistics.totalTenderAllClosed")}:
            </Typography>
            <Chip
              label={totalTenders}
              size="small"
              sx={{
                backgroundColor: "#d1fae5",
                color: "#065f46",
                fontWeight: 600,
                fontSize: "0.75rem",
              }}
            />
          </Box>
        </Box>

        {/* Chart Container */}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                gap: 2,
              }}
            >
              <CircularProgress size={40} sx={{ color: "#3b82f6" }} />
              <Typography variant="body2" sx={{ color: "#6b7280" }}>
                {t("status.loading")}
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer
              width="100%"
              height={isMobile ? 250 : isTablet ? 300 : 350}
            >
              <PieChart>
                <Tooltip content={<CustomTooltip />} />
                <Pie
                  data={tenderStatus}
                  dataKey="total"
                  nameKey="status"
                  cx="50%"
                  cy="50%"
                  outerRadius={isMobile ? 60 : isTablet ? 80 : 100}
                  innerRadius={isMobile ? 40 : isTablet ? 50 : 60}
                  fill="#8884d8"
                  paddingAngle={2}
                  stroke="#ffffff"
                  strokeWidth={2}
                >
                  {tenderStatus.map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Legend content={<CustomLegend />} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Box>

        {/* Footer Info */}
        {!loading && tenderStatus.length > 0 && (
          <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #e5e7eb" }}>
            <Typography
              variant="caption"
              sx={{ color: "#9ca3af", textAlign: "center", display: "block" }}
            >
              {t("statistics.chartSubtitle")}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default BarChartComponent;
